import { isNativeApp } from "@/lib/native"

/** [lat, lng], the order Leaflet takes. */
export type Coords = [number, number]

export type GeolocateOptions = {
  enableHighAccuracy?: boolean
  timeout?: number
  maximumAge?: number
}

/**
 * One position, from whichever API can actually answer.
 *
 * In a browser this is navigator.geolocation, unchanged.
 *
 * Inside the native shell it is @capacitor/geolocation. WKWebView exposes
 * navigator.geolocation, but a web view cannot raise the iOS permission
 * prompt on its own: with no native request the call does not fail loudly,
 * it just never calls back, and "shops near me" sits spinning until the
 * timeout. The plugin asks through CoreLocation, which is the only thing
 * that can put the system dialog on screen.
 *
 * Needs NSLocationWhenInUseUsageDescription in Info.plist. Without the
 * string iOS terminates the app the moment authorization is requested — a
 * crash on a feature people tap, not a silent failure.
 *
 * Rejects on refusal or timeout, so a caller can tell "no" from "not yet".
 */
export async function getCurrentCoords(options: GeolocateOptions = {}): Promise<Coords> {
  const { enableHighAccuracy = true, timeout = 10000, maximumAge = 60 * 1000 } = options

  if (isNativeApp()) {
    // Imported here rather than at the top of the file so the plugin never
    // lands in the web bundle, where it is dead weight.
    const { Geolocation } = await import("@capacitor/geolocation")
    const current = await Geolocation.checkPermissions()
    if (current.location !== "granted" && current.coarseLocation !== "granted") {
      const asked = await Geolocation.requestPermissions({ permissions: ["location"] })
      if (asked.location !== "granted" && asked.coarseLocation !== "granted") {
        throw new Error("Location permission denied")
      }
    }
    const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy, timeout, maximumAge })
    return [pos.coords.latitude, pos.coords.longitude]
  }

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    throw new Error("Geolocation unavailable")
  }

  return new Promise<Coords>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve([pos.coords.latitude, pos.coords.longitude]),
      (err) => reject(err),
      { enableHighAccuracy, timeout, maximumAge },
    )
  })
}
